import {
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';
import {
  type SxProps,
  type Theme,
  autocompleteClasses,
  outlinedInputClasses,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useForkRef } from '@mui/material/utils';
import { useRhfSelectActionOption } from './useRhfSelectActionOption';

export interface RhfSelectOption<V = string> {
  value: V;
  label: string;
}

export type RhfSelectActionDialogProps<R = unknown> = {
  open: boolean;
  onClose: () => void;
  onSuccess: (result: R) => void;
  inputValue?: string;
};

export type RhfSelectActionOption<R = unknown, V = string> = {
  label: string;
  renderDialog: (props: RhfSelectActionDialogProps<R>) => ReactNode;
  onComplete?: (result: R) => void | Promise<void>;
  mapToValue?: (result: R) => V;
};

// MUI positions the clear and popup buttons absolutely to make room for wrapping
// tags, which a single select never has. Keeping them in flow lets a custom end
// adornment sit with them, ahead of the dropdown button.
const INLINE_END_ADORNMENT_SX: SxProps<Theme> = {
  [`&& .${outlinedInputClasses.root}.${autocompleteClasses.inputRoot}`]: {
    paddingRight: '9px',
  },
  [`& .${autocompleteClasses.endAdornment}`]: {
    position: 'static',
    transform: 'none',
  },
};

const LISTBOX_SCROLL_END_THRESHOLD_PX = 200;

function defaultValuesEqual<V>(left: V | '', right: V | ''): boolean {
  return Object.is(left, right);
}

export type ResolvedRhfSelectOption<I, V = string> = RhfSelectOption<V> & {
  item: I;
  isActionOption?: boolean;
};

type RhfSelectOptionsProps<V> = {
  options: RhfSelectOption<V>[];
  items?: never;
  getOptionValue?: never;
  getOptionLabel?: never;
  renderOption?: never;
  renderSelectionAdornment?: never;
  groupBy?: never;
  getOptionDisabled?: never;
  actionOption?: never;
};

type RhfSelectItemsProps<I, R = unknown, V = string> = {
  items: I[];
  getOptionValue: (item: I) => V;
  getOptionLabel: (item: I) => string;
  renderOption?: (option: ResolvedRhfSelectOption<I, V>) => ReactNode;
  renderSelectionAdornment?: (
    option: ResolvedRhfSelectOption<I, V>
  ) => ReactNode;
  groupBy?: (item: I) => string;
  getOptionDisabled?: (item: I) => boolean;
  options?: never;
  actionOption?: RhfSelectActionOption<R, V>;
};

export type RhfSelectProps<
  T extends FieldValues,
  I = string,
  R = unknown,
  V = string,
> = {
  name: FieldPath<T>;
  label: string;
  helperText?: ReactNode; // Shown when the field has no validation error.
  endAdornment?: ReactNode; // Rendered before the dropdown button.
  onValueChange?: (value: V | '') => void;
  valuesEqual?: (left: V | '', right: V | '') => boolean;
  // The text the user is searching for. Empty after a pick, a clear, or blur.
  // freeSolo keeps its query on blur.
  onQueryChange?: (query: string) => void;
  onListboxScrollEnd?: () => void; // Called while the open options list is scrolled near its bottom.
  skipLocalFilter?: boolean; // The loaded options already match the query, including fields outside the label.
  required?: boolean;
  freeSolo?: boolean;
  loading?: boolean;
  disabled?: boolean;
  disableClearable?: boolean;
  openOnFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  sx?: SxProps<Theme>;
} & (RhfSelectOptionsProps<V> | RhfSelectItemsProps<I, R, V>);

function RhfSelect<T extends FieldValues, I = string, R = unknown, V = string>({
  name,
  label,
  helperText,
  endAdornment,
  options,
  items,
  getOptionValue,
  getOptionLabel,
  renderOption,
  renderSelectionAdornment,
  groupBy,
  getOptionDisabled,
  actionOption,
  onValueChange,
  valuesEqual = defaultValuesEqual,
  onQueryChange,
  onListboxScrollEnd,
  skipLocalFilter,
  required,
  freeSolo,
  loading,
  disabled,
  disableClearable,
  openOnFocus,
  inputRef,
  sx,
}: RhfSelectProps<T, I, R, V>) {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: required ? { required: `${label} is required` } : undefined,
  });

  const [typedQuery, setTypedQueryState] = useState('');
  // Text the user is typing. Null shows the selected option's label, including
  // once that option loads after the first paint. freeSolo leaves this unset
  // so MUI keeps the typed value.
  const [typedInput, setTypedInput] = useState<string | null>(null);
  const listboxScrollRef = useRef<{
    node: HTMLElement;
    scrollTop: number;
  } | null>(null);

  const setTypedQuery = useCallback(
    (query: string) => {
      listboxScrollRef.current = null;
      setTypedQueryState(query);
      onQueryChange?.(query);
    },
    [onQueryChange]
  );

  const commitValue = useCallback(
    (nextValue: V | '') => {
      field.onChange(nextValue);
      onValueChange?.(nextValue);
    },
    [field, onValueChange]
  );

  const {
    appendActionOption,
    trySelectActionOption,
    filterOptions,
    actionDialogOpen,
    actionInputValue,
    closeActionDialog,
    handleActionSuccess,
  } = useRhfSelectActionOption<I, R, V>({
    actionOption,
    onCommitValue: commitValue,
    inputValue: typedQuery,
  });

  const resolvedOptions: ResolvedRhfSelectOption<I, V>[] = useMemo(() => {
    const base = options
      ? options.map((opt) => ({
          value: opt.value,
          label: opt.label,
          item: opt as unknown as I,
        }))
      : items.map((item) => ({
          value: getOptionValue(item),
          label: getOptionLabel(item),
          item,
        }));
    return appendActionOption(base);
  }, [options, items, getOptionValue, getOptionLabel, appendActionOption]);

  const selectedValue = useMemo((): ResolvedRhfSelectOption<I, V> | null => {
    const current = (field.value ?? '') as V | '';
    const match = resolvedOptions.find(
      (option) => !option.isActionOption && valuesEqual(option.value, current)
    );
    if (match) {
      return match;
    }
    if (!current) {
      return null;
    }
    return freeSolo
      ? {
          value: current as V,
          label: typeof current === 'string' ? current : '',
          item: current as unknown as I,
        }
      : null;
  }, [field.value, resolvedOptions, freeSolo, valuesEqual]);

  const selectedLabel = selectedValue?.label ?? '';

  // While a value is set, Autocomplete scrolls the open list to the top
  // whenever the options or value change. Runs after its effects.
  useEffect(() => {
    const saved = listboxScrollRef.current;
    if (saved) {
      saved.node.scrollTop = saved.scrollTop;
    }
  }, [resolvedOptions, selectedValue]);

  const mergedInputRef = useForkRef(field.ref, inputRef);

  return (
    <>
      <Autocomplete<
        ResolvedRhfSelectOption<I, V>,
        false,
        boolean | undefined,
        boolean | undefined
      >
        size="small"
        fullWidth
        freeSolo={freeSolo}
        loading={loading}
        disabled={disabled}
        disableClearable={disableClearable ?? (!!required || !!endAdornment)}
        openOnFocus={openOnFocus}
        options={resolvedOptions}
        value={selectedValue}
        // Controlled so a new option object cannot overwrite text being typed.
        // freeSolo already keeps that text unless the value itself changes.
        inputValue={freeSolo ? undefined : (typedInput ?? selectedLabel)}
        onChange={(_, newValue) => {
          // Read the query before clearing it; the action dialog is prefilled
          // from the current search.
          const selectedAction = trySelectActionOption(newValue);
          if (!freeSolo) {
            setTypedInput(null);
          }
          setTypedQuery('');
          if (selectedAction) {
            return;
          }
          commitValue(
            typeof newValue === 'string'
              ? (newValue as V)
              : (newValue?.value ?? '')
          );
        }}
        onInputChange={(_, nextInput, reason) => {
          if (reason === 'input') {
            if (!freeSolo) {
              setTypedInput(nextInput);
            }
            setTypedQuery(nextInput);
            if (freeSolo) {
              commitValue(nextInput as V);
            }
            return;
          }
          // A recreated option, or a page that just arrived, makes MUI reset
          // the input to the selected label. Keep the text being typed.
          if (reason === 'reset') {
            return;
          }
          if (freeSolo) {
            if (reason === 'clear') {
              setTypedQuery('');
            }
            return;
          }
          setTypedInput(null);
          setTypedQuery('');
        }}
        filterOptions={
          skipLocalFilter ? (optionList) => optionList : filterOptions
        }
        groupBy={
          groupBy
            ? (option) => (option.isActionOption ? '' : groupBy(option.item))
            : undefined
        }
        getOptionDisabled={
          getOptionDisabled
            ? (option) =>
                !option.isActionOption && getOptionDisabled(option.item)
            : undefined
        }
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.label
        }
        isOptionEqualToValue={(a, b) => valuesEqual(a.value, b.value)}
        renderOption={(props, option) => (
          <li {...props}>
            {option.isActionOption || !renderOption
              ? option.label
              : renderOption(option)}
          </li>
        )}
        slotProps={
          onListboxScrollEnd
            ? {
                listbox: {
                  onScroll: (event) => {
                    const node = event.currentTarget;
                    const { scrollTop, scrollHeight, clientHeight } = node;
                    listboxScrollRef.current = { node, scrollTop };
                    if (
                      scrollHeight - scrollTop - clientHeight <
                      LISTBOX_SCROLL_END_THRESHOLD_PX
                    ) {
                      onListboxScrollEnd();
                    }
                  },
                },
              }
            : undefined
        }
        renderInput={(params) => {
          const selectionAdornment =
            selectedValue &&
            !selectedValue.isActionOption &&
            renderSelectionAdornment
              ? renderSelectionAdornment(selectedValue)
              : null;
          return (
            <TextField
              {...params}
              label={label}
              required={required}
              error={!!error}
              helperText={error?.message ?? helperText}
              inputRef={mergedInputRef}
              onBlur={() => {
                field.onBlur();
                // MUI skips its blur reset when the text already matches the
                // selected label, which would leave the list filtered.
                if (freeSolo) {
                  return;
                }
                setTypedInput(null);
                setTypedQuery('');
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {selectionAdornment}
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {endAdornment}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
        sx={
          endAdornment
            ? [INLINE_END_ADORNMENT_SX, ...(Array.isArray(sx) ? sx : [sx])]
            : sx
        }
      />
      {actionOption && actionDialogOpen
        ? actionOption.renderDialog({
            open: true,
            onClose: closeActionDialog,
            onSuccess: handleActionSuccess,
            inputValue: actionInputValue,
          })
        : null}
    </>
  );
}

export default RhfSelect;
